import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "hb_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [taxRate, setTaxRate] = useState(0.0975);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (menuItem, qty = 1, sauce = null) => {
    setItems((prev) => {
      const key = menuItem.id + (sauce ? `::${sauce}` : "");
      const existing = prev.find((it) => it.key === key);
      if (existing) {
        return prev.map((it) =>
          it.key === key ? { ...it, quantity: it.quantity + qty } : it
        );
      }
      return [
        ...prev,
        {
          key,
          id: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          image_url: menuItem.image_url,
          category: menuItem.category,
          sauce,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const removeItem = (key) =>
    setItems((prev) => prev.filter((it) => it.key !== key));

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, quantity: qty } : it))
    );
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.price * it.quantity, 0),
    [items]
  );
  const tax = useMemo(() => +(subtotal * taxRate).toFixed(2), [subtotal, taxRate]);
  const total = useMemo(() => +(subtotal + tax).toFixed(2), [subtotal, tax]);
  const count = useMemo(
    () => items.reduce((s, it) => s + it.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        open,
        setOpen,
        addItem,
        removeItem,
        updateQty,
        clear,
        subtotal,
        tax,
        total,
        count,
        taxRate,
        setTaxRate,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
